/**
 * The dashboard's sense of time (F-141, docs/11 §4.3) — PURE, so the two
 * rules are tested rather than eyeballed across a wall of cards:
 *
 *  - a column lists its boards by the NEXT DATE that matters, soonest first
 *    (the kanban used to list oldest-first, which put last month's board
 *    above this week's);
 *  - the "This week" strip names what the calendar owes in the current week,
 *    day by day, from the boards' own dates — the only reminder Kola has.
 */
import { addDateKeyDays, mondayOfWeek, type BoardSummary } from "@kola/shared";

import { orderedSlots, shortsCardChip } from "./shorts-card.js";

/**
 * The date a board is waiting on in its current stage, or null when none:
 * the script date while Scripting (both kinds), then — on a Shorts week —
 * the shoot day, the edit deadline, the next release, exactly what its card
 * chip shows.
 */
export function nextRelevantDate(board: BoardSummary, today: string): string | null {
  if (board.kind === "shorts") return shortsCardChip(board, today)?.dateKey ?? null;
  return board.status === "scripting" ? board.scriptFinishDate : null;
}

/** Soonest date first, undated last, newest first among equals. */
export function sortBoardsForColumn(boards: readonly BoardSummary[], today: string): BoardSummary[] {
  return [...boards].sort((a, b) => {
    const da = nextRelevantDate(a, today);
    const db = nextRelevantDate(b, today);
    if (da !== db) {
      if (da === null) return 1;
      if (db === null) return -1;
      return da.localeCompare(db);
    }
    return b.createdAt.localeCompare(a.createdAt);
  });
}

export type WeekEntryKind = "script" | "shoot" | "edit" | "release";

export interface WeekEntry {
  dateKey: string;
  boardId: string;
  boardName: string;
  kind: WeekEntryKind;
  /** "Script due", "Shoot", "Edit due", "Release" ("Scripts due", "Release 3" on a week of five). */
  label: string;
  /** The day has passed and the step is not done. */
  overdue: boolean;
}

/** Monday to Sunday of the week `today` falls in. */
export function weekWindow(today: string): { from: string; to: string } {
  const from = mondayOfWeek(today);
  return { from, to: addDateKeyDays(from, 6) };
}

const BEFORE_SHOOT = new Set(["scripting", "storyboarding", "request_review", "shooting"]);

/**
 * Everything due in the current week, in date order: script deadlines of
 * boards still scripting, and — for Shorts weeks — the shoot day while the
 * shoot is ahead, the edit deadline once shooting is under way, and each
 * release day of a short not yet out.
 */
export function thisWeekEntries(boards: readonly BoardSummary[], today: string): WeekEntry[] {
  const { from, to } = weekWindow(today);
  const inWeek = (key: string | null): key is string => key !== null && key >= from && key <= to;
  const entries: WeekEntry[] = [];
  const push = (board: BoardSummary, dateKey: string, kind: WeekEntryKind, label: string) =>
    entries.push({
      dateKey,
      boardId: board.id,
      boardName: board.name,
      kind,
      label,
      overdue: dateKey < today,
    });

  for (const board of boards) {
    if (board.status === "scripting" && inWeek(board.scriptFinishDate)) {
      push(board, board.scriptFinishDate, "script", board.kind === "shorts" && board.shorts.length > 1 ? "Scripts due" : "Script due");
    }
    if (board.kind !== "shorts") continue;
    if (BEFORE_SHOOT.has(board.status) && inWeek(board.shootDate)) {
      push(board, board.shootDate, "shoot", "Shoot");
    }
    if ((board.status === "shooting" || board.status === "editing") && inWeek(board.editDueDate)) {
      const allCut = board.shorts.length > 0 && board.shorts.every((s) => s.state === "cut" || s.state === "released");
      if (!allCut) push(board, board.editDueDate, "edit", "Edit due");
    }
    for (const slot of orderedSlots(board)) {
      if (slot.state === "released" || !inWeek(slot.releaseDate)) continue;
      push(board, slot.releaseDate, "release", board.shorts.length === 1 ? "Release" : `Release ${slot.slot}`);
    }
  }
  return entries.sort(
    (a, b) => a.dateKey.localeCompare(b.dateKey) || a.boardName.localeCompare(b.boardName) || a.label.localeCompare(b.label),
  );
}
